import { initialsAvatar } from '../utils/avatar';
import React, { useState, useEffect } from 'react';
import api from '../api/axios';
import ImageUploader from '../components/ImageUploader';

const AcaryasManager = () => {
  const [acaryas, setAcaryas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingAcarya, setEditingAcarya] = useState(null);
  const [isAdding, setIsAdding] = useState(false);

  const initialForm = {
    name: '',
    title: '',
    category: 'Acarya',
    experience: '',
    rating: 4.5,
    reviews: 0,
    location: '',
    skills: [],
    pricing: {
      chat: 0,
      audio: 0,
      video: 0
    },
    chatPackages: [],
    callPackages: [],
    videoPackages: [],
    status: 'offline',
    image: '',
    isFeatured: false,
    email: '',
    password: ''
  };

  const [formData, setFormData] = useState(initialForm);
  const [skillInput, setSkillInput] = useState('');
  const [packageInput, setPackageInput] = useState({ duration: 10, price: 100 });

  useEffect(() => {
    fetchAcaryas();
  }, []);

  const fetchAcaryas = async () => {
    setLoading(true);
    try {
      const res = await api.get('/acaryas');
      if (res.data.success) {
        setAcaryas(res.data.data);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingAcarya) {
        await api.put(`/acaryas/${editingAcarya._id}`, formData);
        alert('Acarya updated successfully');
      } else {
        await api.post('/acaryas', formData);
        alert('Acarya added successfully');
      }
      setEditingAcarya(null);
      setIsAdding(false);
      setFormData(initialForm);
      fetchAcaryas();
    } catch (err) {
      console.error(err);
      alert('Failed to save acarya');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this acarya?')) return;
    try {
      await api.delete(`/acaryas/${id}`);
      fetchAcaryas();
    } catch (err) {
      console.error(err);
      alert('Failed to delete acarya');
    }
  };

  const handleEdit = (acarya) => {
    setEditingAcarya(acarya);
    setFormData({
      ...acarya,
      email: acarya.userId?.email || '',
      password: ''
    });
    setIsAdding(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const addSkill = () => {
    if (skillInput && !formData.skills.includes(skillInput)) {
      setFormData({ ...formData, skills: [...formData.skills, skillInput] });
      setSkillInput('');
    }
  };

  const addPackage = (type) => {
    const key = type === 'video' ? 'videoPackages' : (type === 'call' ? 'callPackages' : 'chatPackages');
    if (packageInput.duration > 0 && packageInput.price > 0) {
      setFormData({
        ...formData,
        [key]: [...(formData[key] || []), packageInput]
      });
      setPackageInput({ duration: 10, price: 100 });
    }
  };

  const removePackage = (index, type) => {
    const key = type === 'video' ? 'videoPackages' : (type === 'call' ? 'callPackages' : 'chatPackages');
    const newPackages = [...(formData[key] || [])];
    newPackages.splice(index, 1);
    setFormData({ ...formData, [key]: newPackages });
  };

  const removeSkill = (skill) => {
    setFormData({ ...formData, skills: formData.skills.filter(s => s !== skill) });
  };

  return (
    <div className="p-8">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h2 className="text-3xl font-bold text-[#2A2858] font-serif">Acarya Manager</h2>
          <p className="text-stone-500 mt-1">Manage consultation acaryas and their services</p>
        </div>
        <button
          onClick={() => {
            setIsAdding(!isAdding);
            setEditingAcarya(null);
            setFormData(initialForm);
          }}
          className="bg-[#2A2858] text-white px-6 py-3 rounded-xl font-bold flex items-center gap-2 hover:bg-[#14122B] transition-all shadow-lg"
        >
          <span className="material-symbols-outlined">{isAdding ? 'close' : 'add'}</span>
          {isAdding ? 'Cancel' : 'Add New Acarya'}
        </button>
      </div>

      {isAdding && (
        <div className="bg-white rounded-3xl p-8 shadow-xl border border-[#E8B84B]/20 mb-12 animate-fade-in">
          <h3 className="text-xl font-bold text-[#2A2858] mb-6 flex items-center gap-2">
            <span className="material-symbols-outlined">person_add</span>
            {editingAcarya ? 'Edit Acarya' : 'Add New Acarya'}
          </h3>
          <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="md:col-span-2">
              <label className="block text-xs font-bold text-stone-500 uppercase tracking-widest mb-2">Full Name</label>
              <input
                type="text"
                required
                value={formData.name}
                onChange={e => setFormData({ ...formData, name: e.target.value })}
                className="w-full px-4 py-3 rounded-xl border border-stone-200 outline-none"
                placeholder="e.g. Acharya Shastry"
              />
            </div>
            <div className="md:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-xs font-bold text-stone-500 uppercase tracking-widest mb-2">Login Email</label>
                <input
                  type="email"
                  required
                  value={formData.email}
                  onChange={e => setFormData({ ...formData, email: e.target.value })}
                  className="w-full px-4 py-3 rounded-xl border border-stone-200 outline-none"
                  placeholder="astrologer@example.com"
                />
              </div>
              <div>
                <label className="block text-xs font-bold text-stone-500 uppercase tracking-widest mb-2">
                  {editingAcarya ? 'New Password (leave blank to keep current)' : 'Login Password'}
                </label>
                <input
                  type="password"
                  required={!editingAcarya}
                  value={formData.password}
                  onChange={e => setFormData({ ...formData, password: e.target.value })}
                  className="w-full px-4 py-3 rounded-xl border border-stone-200 outline-none"
                  placeholder="******"
                />
              </div>
            </div>
            <div>
              <label className="block text-xs font-bold text-stone-500 uppercase tracking-widest mb-2">Title</label>
              <input
                type="text"
                required
                value={formData.title}
                onChange={e => setFormData({ ...formData, title: e.target.value })}
                className="w-full px-4 py-3 rounded-xl border border-stone-200 outline-none"
                placeholder="e.g. Acarya"
              />
            </div>
            <div>
              <label className="block text-xs font-bold text-stone-500 uppercase tracking-widest mb-2">Experience</label>
              <input
                type="text"
                required
                value={formData.experience}
                onChange={e => setFormData({ ...formData, experience: e.target.value })}
                className="w-full px-4 py-3 rounded-xl border border-stone-200 outline-none"
                placeholder="e.g. 15+ Years"
              />
            </div>
            <div>
              <label className="block text-xs font-bold text-stone-500 uppercase tracking-widest mb-2">Location</label>
              <input
                type="text"
                required
                value={formData.location}
                onChange={e => setFormData({ ...formData, location: e.target.value })}
                className="w-full px-4 py-3 rounded-xl border border-stone-200 outline-none"
                placeholder="e.g. Varanasi, India"
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-xs font-bold text-stone-500 uppercase tracking-widest mb-4">Profile Picture</label>
              <div className="flex flex-col md:flex-row gap-8 items-center bg-stone-50 p-6 rounded-2xl border border-stone-100">
                {formData.image ? (
                  <div className="w-32 h-32 rounded-full overflow-hidden border-4 border-[#E8B84B]/20 shadow-xl relative group">
                    <img src={formData.image} alt="Preview" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" />
                    <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                      <span className="text-white text-[8px] font-bold uppercase tracking-widest">Update</span>
                    </div>
                  </div>
                ) : (
                  <div className="w-32 h-32 rounded-full border-4 border-dashed border-stone-200 flex flex-col items-center justify-center bg-white shadow-inner">
                    <span className="material-symbols-outlined text-stone-200 text-5xl">account_circle</span>
                  </div>
                )}
                <div className="flex-1 space-y-4 text-center md:text-left">
                  <ImageUploader
                    buttonText={formData.image ? "Change Profile Photo" : "Upload Profile Photo"}
                    onUploadSuccess={(url) => setFormData({ ...formData, image: url })}
                  />
                  <div className="space-y-1">
                    <p className="text-[11px] text-stone-500 font-medium">Square images (1:1) work best</p>
                    <p className="text-[10px] text-stone-400 italic flex items-center justify-center md:justify-start gap-1">
                      <span className="material-symbols-outlined text-[12px]">verified</span>
                      High-resolution profile photos build trust with devotees
                    </p>
                  </div>
                </div>
              </div>
              <input type="hidden" required value={formData.image} />
            </div>

            <div className="md:col-span-2">
              <label className="block text-xs font-bold text-stone-500 uppercase tracking-widest mb-2">Skills</label>
              <div className="flex gap-2 mb-3">
                <input
                  type="text"
                  value={skillInput}
                  onChange={e => setSkillInput(e.target.value)}
                  className="flex-1 px-4 py-3 rounded-xl border border-stone-200 outline-none"
                  placeholder="Add a skill (e.g. Meditation instruction)"
                  onKeyPress={e => e.key === 'Enter' && (e.preventDefault(), addSkill())}
                />
                <button
                  type="button"
                  onClick={addSkill}
                  className="bg-[#E8B84B] text-[#2A1204] px-6 py-3 rounded-xl font-bold"
                >
                  Add
                </button>
              </div>
              <div className="flex flex-wrap gap-2">
                {formData.skills.map(skill => (
                  <span key={skill} className="bg-stone-100 px-3 py-1.5 rounded-lg text-sm font-medium flex items-center gap-2">
                    {skill}
                    <button type="button" onClick={() => removeSkill(skill)} className="text-red-500 hover:text-red-700">×</button>
                  </span>
                ))}
              </div>
            </div>

            <div className="md:col-span-2 grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
              <div>
                <label className="block text-xs font-bold text-stone-500 uppercase tracking-widest mb-2">Base Chat Rate (₹/min)</label>
                <input
                  type="number"
                  required
                  value={formData.pricing?.chat || 0}
                  onChange={e => setFormData({ ...formData, pricing: { ...formData.pricing, chat: parseInt(e.target.value) } })}
                  className="w-full px-4 py-3 rounded-xl border border-stone-200 outline-none"
                />
              </div>
              <div>
                <label className="block text-xs font-bold text-stone-500 uppercase tracking-widest mb-2">Base Audio Rate (₹/min)</label>
                <input
                  type="number"
                  required
                  value={formData.pricing?.audio || 0}
                  onChange={e => setFormData({ ...formData, pricing: { ...formData.pricing, audio: parseInt(e.target.value) } })}
                  className="w-full px-4 py-3 rounded-xl border border-stone-200 outline-none"
                />
              </div>
              <div>
                <label className="block text-xs font-bold text-stone-500 uppercase tracking-widest mb-2">Base Video Rate (₹/min)</label>
                <input
                  type="number"
                  required
                  value={formData.pricing?.video || 0}
                  onChange={e => setFormData({ ...formData, pricing: { ...formData.pricing, video: parseInt(e.target.value) } })}
                  className="w-full px-4 py-3 rounded-xl border border-stone-200 outline-none"
                />
              </div>
            </div>

            <div className="md:col-span-2 space-y-8">
              {[
                { type: 'chat', label: 'Chat Session Packages', key: 'chatPackages', color: '#E8B84B' },
                { type: 'call', label: 'Audio Call Packages', key: 'callPackages', color: '#3B82F6' },
                { type: 'video', label: 'Video Call Packages', key: 'videoPackages', color: '#8B5CF6' }
              ].map(section => (
                <div key={section.type} className="border-t border-stone-100 pt-6">
                  <label className={`block text-xs font-bold uppercase tracking-widest mb-4`} style={{ color: section.color }}>{section.label}</label>
                  <div className="bg-stone-50 p-4 rounded-xl border border-stone-200 mb-4">
                    <div className="flex gap-4 mb-4">
                      <div className="flex-1">
                        <label className="block text-[10px] font-bold text-stone-500 uppercase mb-1">Duration (Mins)</label>
                        <input
                          type="number"
                          value={packageInput.duration}
                          onChange={e => setPackageInput({ ...packageInput, duration: parseInt(e.target.value) || 0 })}
                          className="w-full px-3 py-2 rounded-lg border border-stone-200 outline-none"
                        />
                      </div>
                      <div className="flex-1">
                        <label className="block text-[10px] font-bold text-stone-500 uppercase mb-1">Total Price (₹)</label>
                        <input
                          type="number"
                          value={packageInput.price}
                          onChange={e => setPackageInput({ ...packageInput, price: parseInt(e.target.value) || 0 })}
                          className="w-full px-3 py-2 rounded-lg border border-stone-200 outline-none"
                        />
                      </div>
                      <div className="flex items-end">
                        <button
                          type="button"
                          onClick={() => addPackage(section.type)}
                          className="px-4 py-2 rounded-lg font-bold h-[38px] transition-all hover:scale-105"
                          /* white ink read fine on the blue and violet
                             sections but vanished on the gold one — the ink
                             now follows the fill's own brightness */
                          style={{ backgroundColor: section.color, color: section.color === '#E8B84B' ? '#2A1204' : '#fff' }}
                        >
                          Add
                        </button>
                      </div>
                    </div>

                    <div className="space-y-2">
                      {(formData[section.key] || []).map((pkg, idx) => (
                        <div key={idx} className="flex justify-between items-center bg-white p-3 rounded border border-stone-100 shadow-sm">
                          <div className="text-sm font-bold text-stone-700">
                            {pkg.duration} Minutes for ₹{pkg.price}
                          </div>
                          <button
                            type="button"
                            onClick={() => removePackage(idx, section.type)}
                            className="text-red-500 hover:text-red-700 text-sm font-bold"
                          >
                            Remove
                          </button>
                        </div>
                      ))}
                      {(!formData[section.key] || formData[section.key].length === 0) && (
                        <div className="text-xs text-stone-400 italic">No {section.type} packages added.</div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="flex items-center gap-8">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.isFeatured}
                  onChange={e => setFormData({ ...formData, isFeatured: e.target.checked })}
                  className="w-4 h-4 text-[#2A2858] rounded focus:ring-[#2A2858]"
                />
                <span className="text-xs font-bold text-stone-500 uppercase tracking-widest">Featured Acarya</span>
              </label>
              <div className="flex items-center gap-2">
                <label className="text-xs font-bold text-stone-500 uppercase tracking-widest">Status:</label>
                <select
                  value={formData.status}
                  onChange={e => setFormData({ ...formData, status: e.target.value })}
                  className="px-3 py-1.5 rounded-lg border border-stone-200 outline-none text-xs font-bold"
                >
                  <option value="online">Online</option>
                  <option value="offline">Offline</option>
                </select>
              </div>
            </div>

            <div className="md:col-span-2 flex justify-end gap-4 mt-4">
              <button
                type="button"
                onClick={() => { setIsAdding(false); setEditingAcarya(null); }}
                className="px-8 py-3 rounded-xl font-bold text-stone-500 hover:bg-stone-100 transition-all"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="bg-[#E8B84B] text-[#2A1204] px-10 py-3 rounded-xl font-bold hover:bg-[#B8860B] hover:text-white transition-all shadow-lg"
              >
                {editingAcarya ? 'Update Acarya' : 'Add Acarya'}
              </button>
            </div>
          </form>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-20">
          <div className="w-12 h-12 border-4 border-[#2A2858]/20 border-t-[#2A2858] rounded-full animate-spin"></div>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {acaryas.map((acarya) => (
            <div key={acarya._id} className="bg-white rounded-3xl overflow-hidden shadow-md border border-stone-100 group">
              <div className="p-6 flex gap-6">
                <div className="w-24 h-24 rounded-2xl overflow-hidden shrink-0 border border-stone-100 shadow-inner">
                  <img src={acarya.image || initialsAvatar(acarya.name)} alt={acarya.name} className="w-full h-full object-cover" />
                </div>
                <div className="flex-1">
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <h4 className="text-xl font-bold text-stone-800 font-serif">{acarya.name}</h4>
                      <div className="text-[10px] text-[#2A2858] font-bold uppercase tracking-widest">{acarya.title}</div>
                      <div className="text-[9px] text-stone-400 font-bold uppercase tracking-[0.2em] mt-1">{acarya.userId?.email || 'No login linked'}</div>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleEdit(acarya)}
                        className="w-8 h-8 rounded-full bg-stone-100 text-stone-600 flex items-center justify-center hover:bg-[#2A2858] hover:text-white transition-all"
                      >
                        <span className="material-symbols-outlined text-[18px]">edit</span>
                      </button>
                      <button
                        onClick={() => handleDelete(acarya._id)}
                        className="w-8 h-8 rounded-full bg-stone-100 text-red-600 flex items-center justify-center hover:bg-red-600 hover:text-white transition-all"
                      >
                        <span className="material-symbols-outlined text-[18px]">delete</span>
                      </button>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 mb-4">
                    <div className="flex items-center gap-1">
                      <span className="material-symbols-outlined text-[#E8B84B] text-[16px]" style={{ fontVariationSettings: "'FILL' 1" }}>star</span>
                      <span className="text-sm font-bold">{acarya.rating}</span>
                    </div>
                    <span className="text-stone-300">|</span>
                    <span className="text-xs text-stone-500 font-medium">{acarya.experience} exp.</span>
                    <span className="text-stone-300">|</span>
                    <div className={`flex items-center gap-1 ${acarya.status === 'online' ? 'text-green-500' : 'text-stone-400'}`}>
                      <div className={`w-2 h-2 rounded-full ${acarya.status === 'online' ? 'bg-green-500' : 'bg-stone-300'}`}></div>
                      <span className="text-[10px] font-bold uppercase tracking-widest">{acarya.status}</span>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {acarya.skills.slice(0, 3).map(skill => (
                      <span key={skill} className="text-[9px] bg-stone-50 px-2 py-1 rounded text-stone-500 font-bold uppercase">{skill}</span>
                    ))}
                    {acarya.skills.length > 3 && <span className="text-[9px] text-stone-400 font-bold">+{acarya.skills.length - 3} more</span>}
                  </div>
                </div>
              </div>
              <div className="px-6 py-4 bg-stone-50 flex justify-between items-center border-t border-stone-100">
                <div className="flex gap-4">
                  <div className="flex flex-col">
                    <span className="text-[8px] text-stone-400 font-bold uppercase">Chat</span>
                    <span className="text-xs font-bold text-[#2A2858]">{acarya.chatPackages?.length || 0}</span>
                  </div>
                  <div className="flex flex-col">
                    <span className="text-[8px] text-stone-400 font-bold uppercase">Audio</span>
                    <span className="text-xs font-bold text-blue-600">{acarya.callPackages?.length || 0}</span>
                  </div>
                  <div className="flex flex-col">
                    <span className="text-[8px] text-stone-400 font-bold uppercase">Video</span>
                    <span className="text-xs font-bold text-purple-600">{acarya.videoPackages?.length || 0}</span>
                  </div>
                  <div className="flex flex-col">
                    <span className="text-[8px] text-stone-400 font-bold uppercase">Base Rate</span>
                    <span className="text-xs font-bold text-stone-600">₹{acarya.pricing?.chat || 0}/min</span>
                  </div>
                </div>
                {acarya.isFeatured && (
                  <span className="bg-[#E8B84B]/10 text-[#E8B84B] text-[9px] px-2 py-1 rounded font-black uppercase tracking-tighter">Featured</span>
                )}
              </div>
            </div>
          ))}
          {acaryas.length === 0 && (
            <div className="lg:col-span-2 py-20 text-center bg-white rounded-3xl border border-dashed border-stone-300">
              <span className="material-symbols-outlined text-5xl text-stone-200 mb-4">psychology</span>
              <p className="text-stone-400 font-bold">No astrologers found. Add your first acarya to get started.</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AcaryasManager;
